import React, { useEffect, useState } from "react";
import api from "@/lib/api";

export const ugx = (n) => `UGX ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
export const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

export const ORDER_STATUS = {
  REQUESTED: { label: "Requested", tone: "bg-slate-100 text-slate-700 border-slate-200" },
  RELEASED: { label: "Released", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  PICKED_UP: { label: "With rider", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  DELIVERED: { label: "Delivered · empty due", tone: "bg-violet-50 text-violet-700 border-violet-200" },
  CLOSED: { label: "Closed", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  CANCELLED: { label: "Cancelled", tone: "bg-red-50 text-red-600 border-red-200" },
};

export const OrderStatusPill = ({ status, testId }) => {
  const s = ORDER_STATUS[status] || ORDER_STATUS.REQUESTED;
  return <span className={`inline-flex px-2 py-0.5 rounded-sm text-[11px] font-semibold border whitespace-nowrap ${s.tone}`} data-testid={testId}>{s.label}</span>;
};

export const STOCK_REASONS = {
  RESTOCK: "New full cylinders received",
  REFILLED_EMPTIES: "Empties refilled (empty → full)",
  WALKIN_SALE: "Regular walk-in sale (full out, empty in)",
  EMPTIES_COLLECTED: "Empties sent to filling plant",
  CORRECTION: "Manual correction",
  ORDER_RELEASE: "Released to Gasify order",
  ORDER_EMPTY_RETURN: "Empty returned by Gasify",
  ORDER_CANCEL: "Order cancelled — back to stock",
  RECONCILIATION: "End-of-day count adjustment",
};

export const useStations = () => {
  const [stations, setStations] = useState([]);
  const load = () => api.get("/stations").then(({ data }) => setStations(data.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  return [stations, load];
};

const Meter = ({ label, available, total, used, usedLabel, testId, hint }) => {
  const pct = total > 0 ? Math.max(0, Math.min(100, (available / total) * 100)) : 0;
  const tone = pct < 15 ? "bg-red-500" : pct < 35 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="flex items-baseline gap-2 mt-2">
        <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums" data-testid={`${testId}-available`}>{ugx(available)}</div>
        <div className="text-xs text-slate-400">available of {ugx(total)}</div>
      </div>
      <div className="h-2 bg-slate-100 rounded-full mt-3 overflow-hidden"><div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>
      <div className="flex justify-between text-[11px] text-slate-500 mt-2 tabular-nums">
        <span>{usedLabel}: <span className="text-slate-800 font-medium">{ugx(used)}</span></span>
        <span>{pct.toFixed(0)}% free</span>
      </div>
      {hint && <div className="text-[10px] text-slate-400 mt-2">{hint}</div>}
    </div>
  );
};

export const FacilityMeters = ({ facility }) => (
  <div className="grid gap-4 md:grid-cols-2">
    <Meter label="Stabex LPG credit line" testId="facility-credit" available={facility?.credit_available} total={facility?.credit_limit}
      used={facility?.credit_used} usedLabel="Owed to Stabex" hint="Each refill released uses credit; repayments restore it." />
    <Meter label="Bank guarantee · cylinder cover" testId="facility-cover" available={facility?.cover_available} total={facility?.guarantee_face_value}
      used={facility?.cover_used} usedLabel="Cylinders out" hint="Each full cylinder collected uses cover; each empty returned restores it." />
  </div>
);

export const StockTable = ({ rows, testId = "stock-table" }) => (
  <table className="w-full text-sm" data-testid={testId}>
    <thead className="text-[10px] uppercase tracking-widest text-slate-500">
      <tr><th className="text-left py-1.5">Cylinder</th><th className="text-right py-1.5">Full</th><th className="text-right py-1.5">Empty</th></tr>
    </thead>
    <tbody>
      {(rows || []).map((r) => (
        <tr key={r.cylinder_type_id} className="border-t border-slate-100">
          <td className="py-1.5 text-slate-700">{r.cylinder_type}</td>
          <td className={`py-1.5 text-right font-semibold tabular-nums ${r.full === 0 ? "text-red-600" : "text-slate-900"}`} data-testid={`${testId}-full-${r.cylinder_type.replace(/\s+/g, "")}`}>{r.full}</td>
          <td className="py-1.5 text-right tabular-nums text-slate-600" data-testid={`${testId}-empty-${r.cylinder_type.replace(/\s+/g, "")}`}>{r.empty}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
